import {
    ICashbackOfferBonus,
    ICashbackOfferBonusPeriod,
    ICreateCashbackOfferItem,
    IUpdateCashbackOfferPayload,
} from '@/components_v3/affiliate/contest/entities/cashback-offer/model/types';
import { bonusesKey, specialPeriodKey } from '@/components_v3/affiliate/contest/model/draft.lib';
import type { ContestEditSnapshot, ContestOfferDraft } from '@/components_v3/affiliate/contest/model/types';
import { isContestGoalIdValid } from '@/components_v3/affiliate/contest/shared/lib/map-goals.lib';
import { normalizeContestPeriodDateToYmd } from '@/components_v3/affiliate/contest/shared/lib/parse-contest-period-range.lib';
import {
    isContestSavedPlanCleared,
    parseOfferMoneyAmount,
} from '@/components_v3/affiliate/contest/shared/lib/parse-offer-money.lib';

export const CONTEST_SAVED_PLAN_CLEAR_MESSAGE =
    'Нельзя очистить сохранённый план — укажите значение или отмените изменения';

/**
 * Валидация спец-периода перед сохранением (контракт contest-service-v2).
 *
 * API хранит переопределение только в bonuses[].periods[{ from, to, bonus }].
 * Базовый bonus на цель обязателен в той же записи bonuses — без него periods не сохранятся.
 */
export function getContestSpecialPeriodValidationMessage(draft: ContestOfferDraft): string | null {
    if (!draft.specialPeriodEnabled) {
        return null;
    }

    if (!draft.specialPeriod) {
        return 'Выберите период в календаре';
    }

    const goalsWithPeriodBonus = draft.goals.filter(
        (goal) => parseOfferMoneyAmount(goal.periodBonus) != null,
    );

    if (!goalsWithPeriodBonus.length) {
        return 'Заполните «Спец-период бонуса» хотя бы у одной цели';
    }

    const hasPeriodBonusWithoutBaseBonus = goalsWithPeriodBonus.some(
        (goal) => parseOfferMoneyAmount(goal.bonus) == null,
    );

    if (hasPeriodBonusWithoutBaseBonus) {
        return 'Для спец-периода бонуса нужен базовый «Бонус за лид» в той же строке';
    }

    return null;
}

export function getContestOfferSaveValidationError(
    draft: ContestOfferDraft,
    options?: { hadSavedPlan?: boolean },
): string | null {
    if (isContestSavedPlanCleared(options?.hadSavedPlan ?? false, draft.plan)) {
        return CONTEST_SAVED_PLAN_CLEAR_MESSAGE;
    }

    return getContestSpecialPeriodValidationMessage(draft);
}

function buildGoalPeriods(
    draft: ContestOfferDraft,
    periodBonus: number,
): ICashbackOfferBonusPeriod[] | undefined {
    const { specialPeriodEnabled, specialPeriod } = draft;

    if (!specialPeriodEnabled || !specialPeriod) {
        return undefined;
    }

    const from = normalizeContestPeriodDateToYmd(specialPeriod.from);
    const to = normalizeContestPeriodDateToYmd(specialPeriod.to);

    if (!from || !to) {
        return undefined;
    }

    return [{ from, to, bonus: periodBonus }];
}

/**
 * Сбор bonuses для POST/PATCH (LK-8291 / contest-service-v2).
 *
 * bonus — базовый бонус на весь месяц (обязателен в записи bonuses).
 * periods — опционально: один диапазон from/to (Y-m-d) + bonus периода на цель.
 * Один датапикер на оффер → одинаковые from/to у всех целей с заполненным periodBonus.
 * Цель без periodBonus уходит только с bonus, без periods.
 */
function buildBonusesFromGoals(draft: ContestOfferDraft): ICashbackOfferBonus[] {
    return draft.goals
        .map((goal) => {
            const bonus = parseOfferMoneyAmount(goal.bonus);

            if (bonus == null || !isContestGoalIdValid(goal.legacy_id)) {
                return null;
            }

            const item: ICashbackOfferBonus = {
                goal_id: goal.legacy_id,
                bonus,
            };

            const periodBonus = parseOfferMoneyAmount(goal.periodBonus);

            if (periodBonus != null) {
                const periods = buildGoalPeriods(draft, periodBonus);

                if (periods) {
                    item.periods = periods;
                }
            }

            return item;
        })
        .filter((item): item is ICashbackOfferBonus => item != null);
}

function isBonusesPayloadChanged(
    draft: ContestOfferDraft,
    snapshot: ContestEditSnapshot | null,
): boolean {
    if (!snapshot) {
        return false;
    }

    return bonusesKey(draft.goals) !== snapshot.initialBonusesKey
        || draft.specialPeriodEnabled !== snapshot.initialSpecialPeriodEnabled
        || specialPeriodKey(draft.specialPeriod) !== snapshot.initialSpecialPeriodKey;
}

export function buildPostOfferItem(draft: ContestOfferDraft): ICreateCashbackOfferItem {
    const plan = parseOfferMoneyAmount(draft.plan);
    const item: ICreateCashbackOfferItem = {
        offer_id: draft.offerId as number,
        ...(plan != null ? { custom_goal: plan } : {}),
    };
    const bonuses = buildBonusesFromGoals(draft);

    if (bonuses.length) {
        item.bonuses = bonuses;
    }

    return item;
}

export function buildPatchPayload(
    draft: ContestOfferDraft,
    snapshot: ContestEditSnapshot | null,
    month: string,
): IUpdateCashbackOfferPayload | null {
    const payload: IUpdateCashbackOfferPayload = { month };

    if (snapshot && draft.plan !== snapshot.draft.plan) {
        const plan = parseOfferMoneyAmount(draft.plan);

        if (plan != null) {
            payload.custom_goal = plan;
        }
    }

    if (isBonusesPayloadChanged(draft, snapshot)) {
        payload.bonuses = buildBonusesFromGoals(draft);
    }

    if (payload.custom_goal === undefined && payload.bonuses === undefined) {
        return null;
    }

    return payload;
}
